import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, KeyRound, Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

// One panel for a tenant's API keys, used by the tenant's Settings → API Keys
// and by the platform admin's tenant page. Each passes its own endpoints.

export type ApiKey = {
  id: string;
  name: string;
  key_prefix: string;
  permissions: string[];
  allowed_ips: string[];
  expires_at: string | null;
  is_active: boolean;
  last_used_at: string | null;
  usage_count: number;
  created_at: string;
  created_by_name?: string;
};

export type NewApiKey = {
  name: string;
  permissions: string[];
  allowed_ips: string[];
  expires_at: string | null;
};

export type ApiKeysEndpoints = {
  queryKey: readonly unknown[];
  list: () => Promise<ApiKey[]>;
  create: (data: NewApiKey) => Promise<ApiKey & { key: string }>;
  revoke: (id: string) => Promise<unknown>;
};

const SCOPES = [
  { value: "calls:write", label: "Start and cancel calls" },
  { value: "calls:read", label: "Read calls" },
];

function errorText(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data as
    | { error?: { message?: string; details?: Record<string, unknown> }; detail?: string }
    | undefined;
  const first = data?.error?.details && Object.values(data.error.details)[0];
  if (Array.isArray(first) && typeof first[0] === "string") return first[0];
  return data?.error?.message ?? data?.detail ?? "Something went wrong.";
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

function status(k: ApiKey): { label: string; className: string } {
  if (!k.is_active) return { label: "Revoked", className: "bg-muted text-muted-foreground" };
  if (k.expires_at && new Date(k.expires_at) < new Date())
    return { label: "Expired", className: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400" };
  return { label: "Active", className: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400" };
}

export default function ApiKeysPanel({ endpoints }: { endpoints: ApiKeysEndpoints }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: endpoints.queryKey, queryFn: endpoints.list });
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);

  const revoke = useMutation({
    mutationFn: (id: string) => endpoints.revoke(id),
    onSuccess: () => {
      toast.success("Key revoked. Apps using it can no longer connect.");
      setRevokeTarget(null);
      qc.invalidateQueries({ queryKey: endpoints.queryKey });
    },
    onError: (e) => toast.error(errorText(e)),
  });

  const keys = q.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Outside apps use an API key to start calls with your agents and read their results. Calls they
          place use this workspace's credit and appear in Calls, like any other call.
        </p>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />
          Create key
        </Button>
      </div>

      {q.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : keys.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          <KeyRound className="size-5" />
          No API keys yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Key</th>
                <th className="px-3 py-2 font-medium">Can</th>
                <th className="px-3 py-2 font-medium">Last used</th>
                <th className="px-3 py-2 font-medium">Uses</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const s = status(k);
                return (
                  <tr key={k.id} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <div className="font-medium">{k.name}</div>
                      <div className="text-xs text-muted-foreground">
                        Created {when(k.created_at)}
                        {k.created_by_name ? ` by ${k.created_by_name}` : ""}
                        {k.expires_at ? ` · expires ${when(k.expires_at)}` : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{k.key_prefix}…</td>
                    <td className="px-3 py-2 text-xs">
                      {k.permissions.length === 0
                        ? "Nothing (no scopes)"
                        : k.permissions.map((p) => SCOPES.find((x) => x.value === p)?.label ?? p).join(", ")}
                      {k.allowed_ips.length > 0 && (
                        <div className="text-muted-foreground">Only from {k.allowed_ips.join(", ")}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{when(k.last_used_at)}</td>
                    <td className="px-3 py-2 text-xs">{k.usage_count.toLocaleString()}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${s.className}`}>{s.label}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {k.is_active && (
                        <Button variant="ghost" size="sm" onClick={() => setRevokeTarget(k)}>
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        endpoints={endpoints}
        onCreated={(plain) => {
          setCreateOpen(false);
          setCreated(plain);
          qc.invalidateQueries({ queryKey: endpoints.queryKey });
        }}
      />
      <ShowOnceDialog plain={created} onClose={() => setCreated(null)} />

      <Dialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Revoke “{revokeTarget?.name}”?</DialogTitle>
            <DialogDescription>
              Every app using this key stops working straight away. This cannot be undone; create a new key
              if you need one.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={revoke.isPending}
              onClick={() => revokeTarget && revoke.mutate(revokeTarget.id)}
            >
              Revoke key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateKeyDialog({
  open,
  onOpenChange,
  endpoints,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  endpoints: ApiKeysEndpoints;
  onCreated: (plain: string) => void;
}) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>(SCOPES.map((s) => s.value));
  const [expires, setExpires] = useState("");
  const [ips, setIps] = useState("");

  function reset() {
    setName("");
    setScopes(SCOPES.map((s) => s.value));
    setExpires("");
    setIps("");
  }

  const create = useMutation({
    mutationFn: () =>
      endpoints.create({
        name: name.trim(),
        permissions: scopes,
        allowed_ips: ips
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        // The end of the chosen day, in the viewer's time zone.
        expires_at: expires ? new Date(`${expires}T23:59:59`).toISOString() : null,
      }),
    onSuccess: (key) => {
      reset();
      onCreated(key.key);
    },
    onError: (e) => toast.error(errorText(e)),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create API key</DialogTitle>
          <DialogDescription>Give it the name of the app that will use it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="key-name">Name</Label>
            <Input
              id="key-name"
              value={name}
              maxLength={100}
              placeholder="e.g. Shift Filla"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>What it can do</Label>
            {SCOPES.map((s) => (
              <label key={s.value} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={scopes.includes(s.value)}
                  onCheckedChange={(c) =>
                    setScopes((cur) => (c ? [...cur, s.value] : cur.filter((x) => x !== s.value)))
                  }
                />
                {s.label}
              </label>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="key-expires">Expires (optional)</Label>
            <Input id="key-expires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="key-ips">Only allow these IP addresses (optional)</Label>
            <Textarea
              id="key-ips"
              rows={2}
              value={ips}
              placeholder="203.0.113.5, 2001:db8::1"
              onChange={(e) => setIps(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Leave empty to allow any address.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
            Create key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShowOnceDialog({ plain, onClose }: { plain: string | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <Dialog
      open={!!plain}
      onOpenChange={(o) => {
        if (!o) {
          setCopied(false);
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Copy your API key now</DialogTitle>
          <DialogDescription>
            This is the only time it is shown. Store it somewhere safe, such as the app's secret settings.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input readOnly value={plain ?? ""} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
          <Button
            variant="outline"
            size="icon"
            aria-label="Copy key"
            onClick={() => {
              if (!plain) return;
              navigator.clipboard.writeText(plain);
              setCopied(true);
            }}
          >
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Apps send it in the <code>X-API-Key</code> header.
        </p>
        <DialogFooter>
          <Button
            onClick={() => {
              setCopied(false);
              onClose();
            }}
          >
            I've copied it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
